import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/AuthProvider';
import { useActiveChild } from '@/contexts/ActiveChildContext';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { StudentProfile, User as UserType } from '@/types';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { BeltAvatar } from '@/components/ui/belt-avatar';
import { BeltBadge } from '@/components/ui/BeltBadge';
import { User, Settings, LogOut, Medal, Wallet, Globe, Trophy, UserPlus, Check, ArrowLeft, Users, Star, CreditCard, Bell } from 'lucide-react';
import { AddKidDialog } from '@/features/dashboard/components/AddKidDialog';
import { useGamification } from '@/hooks/useGamification';

import { useTranslation } from 'react-i18next';

export const UserProfileDropdown = () => {
    const { t } = useTranslation();
    const { currentUser, signOut } = useAuth();
    const { activeChildId, setActiveChild, clearActiveChild } = useActiveChild();
    const { getUserLevel } = useGamification();
    const navigate = useNavigate();
    const [profile, setProfile] = useState<StudentProfile | null>(null);
    const [children, setChildren] = useState<UserType[]>([]);
    const [showAddKid, setShowAddKid] = useState(false);

    // Get level info for mobile XP display
    const totalScore = currentUser?.totalScore || 0;
    const levelInfo = getUserLevel(totalScore);

    useEffect(() => {
        const fetchProfile = async () => {
            if (!currentUser?.uid) return;

            try {
                const docRef = doc(db, 'studentProfiles', currentUser.uid);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setProfile(docSnap.data() as StudentProfile);
                }
            } catch (err) {
                console.error("Error fetching profile for dropdown", err);
            }
        };
        fetchProfile();
    }, [currentUser]);

    // Fetch children if user is a parent
    useEffect(() => {
        const fetchChildren = async () => {
            if (!currentUser?.uid || currentUser.role !== 'parent') return;

            try {
                const q = query(collection(db, 'users'), where('parentId', '==', currentUser.uid));
                const snapshot = await getDocs(q);
                const fetchedChildren: UserType[] = [];
                snapshot.forEach(docSnap => {
                    const data = docSnap.data() as Omit<UserType, 'uid'>;
                    fetchedChildren.push({ ...data, uid: docSnap.id } as UserType);
                });
                setChildren(fetchedChildren);
            } catch (err) {
                console.error("Error fetching children:", err);
            }
        };
        fetchChildren();
    }, [currentUser]);

    const handleSignOut = async () => {
        await signOut();
        navigate('/login');
    };

    if (!currentUser) return null;

    const initials = currentUser.displayName
        ? currentUser.displayName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
        : 'U';

    const activeChild = activeChildId ? children.find(c => c.uid === activeChildId) : null;

    // Display Target: Active Child OR Current User
    const displayPhoto = activeChild ? (activeChild.photoURL || undefined) : (currentUser.photoURL || profile?.profilePhotoURL);
    const displayBelt = activeChild ? activeChild.belt : (currentUser.belt || profile?.belt);
    const displayStripes = activeChild ? activeChild.stripes : (currentUser.stripes || profile?.stripes);
    const displayFallback = activeChild ? activeChild.displayName?.substring(0, 2).toUpperCase() : initials;

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                        <BeltAvatar
                            src={displayPhoto}
                            fallback={displayFallback || 'U'}
                            belt={displayBelt}
                            stripes={displayStripes}
                            size="md"
                        />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-72" align="end" forceMount>
                    <DropdownMenuLabel className="font-normal">
                        <div className="flex flex-col space-y-1">
                            <p className="text-sm font-medium leading-none">{currentUser.displayName}</p>
                            <p className="text-xs leading-none text-muted-foreground">
                                {currentUser.email}
                            </p>
                        </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />

                    {/* Mobile-only XP & Level Section */}
                    <div className="md:hidden p-3 bg-gradient-to-r from-amber-50 to-orange-50 mx-2 rounded-lg mb-2">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <Star className="h-5 w-5 text-amber-500 fill-amber-500" />
                                <span className="font-bold text-amber-900">{totalScore.toLocaleString()} XP</span>
                            </div>
                            {levelInfo.current && (
                                <span className="text-sm font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                                    Level {levelInfo.current.level}
                                </span>
                            )}
                        </div>
                        {levelInfo.next && (
                            <div className="space-y-1">
                                <div className="w-full h-2 bg-amber-200 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all duration-300"
                                        style={{ width: `${levelInfo.progress}%` }}
                                    />
                                </div>
                                <p className="text-xs text-amber-600 text-center">
                                    {t('gamification.xp_to_level', '{{points}} XP to Level {{level}}', {
                                        points: (levelInfo.next.pointsRequired - totalScore).toLocaleString(),
                                        level: levelInfo.next.level
                                    })}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Belt Rank Section */}
                    {profile?.belt && (
                        <div className="p-2 flex justify-center">
                            <BeltBadge
                                rank={profile.belt}
                                stripes={profile.stripes || 0}
                                size="lg"
                            />
                        </div>
                    )}

                    {/* Badges/Achievements Section */}
                    {profile?.achievements && profile.achievements.length > 0 && (
                        <div className="p-2">
                            <p className="text-xs font-semibold text-muted-foreground mb-2 px-1">{t('gamification.badges', 'Badges')}</p>
                            <div className="flex flex-wrap gap-2">
                                {profile.achievements.map((badge, idx) => (
                                    <div key={idx} className="flex items-center gap-1 bg-yellow-50 text-yellow-700 px-2 py-1 rounded-full text-xs border border-yellow-200">
                                        <Medal className="h-3 w-3" />
                                        {badge}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {(profile?.belt || (profile?.achievements && profile.achievements.length > 0)) && (
                        <DropdownMenuSeparator />
                    )}

                    {/* PARENT CHILDREN SWITCHER */}
                    {currentUser.role === 'parent' && children.length > 0 && (
                        <>
                            <DropdownMenuLabel className="text-xs text-muted-foreground flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                {t('parent_dashboard.my_children', 'My Children')}
                            </DropdownMenuLabel>
                            <DropdownMenuGroup>
                                {children.map((child) => (
                                    <DropdownMenuItem
                                        key={child.uid}
                                        onClick={() => {
                                            setActiveChild(child.uid);
                                            navigate('/dashboard');
                                        }}
                                        className="cursor-pointer"
                                    >
                                        <User className="mr-2 h-4 w-4" />
                                        <span>{child.displayName || 'Child'}</span>
                                        {activeChildId === child.uid && (
                                            <Check className="ml-auto h-4 w-4 text-primary" />
                                        )}
                                    </DropdownMenuItem>
                                ))}
                                <DropdownMenuItem
                                    onClick={() => setShowAddKid(true)}
                                    className="cursor-pointer text-primary"
                                >
                                    <UserPlus className="mr-2 h-4 w-4" />
                                    <span>{t('parent_dashboard.add_child', 'Add Child')}</span>
                                </DropdownMenuItem>
                                {activeChildId && (
                                    <DropdownMenuItem
                                        onClick={() => {
                                            clearActiveChild();
                                            navigate('/dashboard');
                                        }}
                                        className="cursor-pointer"
                                    >
                                        <ArrowLeft className="mr-2 h-4 w-4 rtl:rotate-180" />
                                        <span>{t('parent_dashboard.view_as_parent', 'View as Parent')}</span>
                                    </DropdownMenuItem>
                                )}
                            </DropdownMenuGroup>
                            <DropdownMenuSeparator />
                        </>
                    )}

                    {/* Context-aware menu items */}
                    {(() => {
                        // Determine which user context we're operating in
                        const activeChild = children.find(c => c.uid === activeChildId);
                        const effectiveUserId = activeChildId || currentUser.uid;
                        const isViewingChild = !!activeChild;
                        const effectiveName = activeChild
                            ? (activeChild.displayName || '').split(' ')[0]
                            : currentUser.displayName;

                        return (
                            <>
                                {isViewingChild && (
                                    <DropdownMenuLabel className="text-xs text-amber-600 bg-amber-50 rounded mx-2 py-1.5 px-2 flex items-center justify-between gap-1">
                                        <div className="flex items-center gap-1">
                                            <User className="h-3 w-3" />
                                            {t('parent_dashboard.viewing_as_user', 'Viewing as: {{name}}', { name: effectiveName })}
                                        </div>
                                        {activeChild?.belt && (
                                            <div className="transform scale-90 origin-right">
                                                <BeltBadge rank={activeChild.belt} stripes={activeChild.stripes || 0} size="sm" showStripes={false} />
                                            </div>
                                        )}
                                    </DropdownMenuLabel>
                                )}
                                <DropdownMenuGroup>
                                    <DropdownMenuItem asChild>
                                        <Link
                                            to={`/u/${effectiveUserId}`}
                                            className="cursor-pointer"
                                        >
                                            <Globe className="mr-2 h-4 w-4" />
                                            <span>
                                                {isViewingChild
                                                    ? t('profile.users_profile', "{{name}}'s Profile", { name: effectiveName })
                                                    : t('profile.public_profile', 'Public Profile')}
                                            </span>
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <Link to={isViewingChild ? `/notifications?userId=${effectiveUserId}` : '/notifications'} className="cursor-pointer">
                                            <Bell className="mr-2 h-4 w-4" />
                                            <span>
                                                {isViewingChild
                                                    ? t('notifications.users_notifications', "{{name}}'s Notifications", { name: effectiveName })
                                                    : t('mobile.my_notifications', 'My Notifications')}
                                            </span>
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <Link to={`/users/${effectiveUserId}`} className="cursor-pointer">
                                            <Trophy className="mr-2 h-4 w-4 text-amber-600" />
                                            <span>
                                                {isViewingChild
                                                    ? t('gamification.users_badges', "{{name}}'s Badges", { name: effectiveName })
                                                    : t('mobile.my_badges', 'My Badges')}
                                            </span>
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <Link to={isViewingChild ? `/wallet?userId=${effectiveUserId}` : '/wallet'} className="cursor-pointer">
                                            <Wallet className="mr-2 h-4 w-4" />
                                            <span>
                                                {isViewingChild
                                                    ? t('finance.users_wallet', "{{name}}'s Wallet", { name: effectiveName })
                                                    : t('mobile.my_wallet', 'My Wallet')}
                                            </span>
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <Link to={isViewingChild ? `/my-subscription?userId=${effectiveUserId}` : '/my-subscription'} className="cursor-pointer">
                                            <CreditCard className="mr-2 h-4 w-4 text-primary" />
                                            <span>
                                                {isViewingChild
                                                    ? t('subscriptions.users_subscription', "{{name}}'s Subscription", { name: effectiveName })
                                                    : t('mobile.my_subscription', 'My Subscription')}
                                            </span>
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <Link to={`/profile/${effectiveUserId}`} className="cursor-pointer">
                                            <User className="mr-2 h-4 w-4" />
                                            <span>
                                                {isViewingChild
                                                    ? t('profile.edit_users_profile', "Edit {{name}}'s Profile", { name: effectiveName })
                                                    : t('mobile.edit_profile', 'Edit Profile')}
                                            </span>
                                        </Link>
                                    </DropdownMenuItem>
                                    {currentUser.role === 'super_admin' && (
                                        <DropdownMenuItem asChild>
                                            <Link to="/settings" className="cursor-pointer">
                                                <Settings className="mr-2 h-4 w-4" />
                                                <span>{t('settings.title', 'Settings')}</span>
                                            </Link>
                                        </DropdownMenuItem>
                                    )}
                                </DropdownMenuGroup>
                            </>
                        );
                    })()}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50">
                        <LogOut className="mr-2 h-4 w-4" />
                        <span>{t('auth.logout', 'Log out')}</span>
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            {/* Add Kid Dialog */}
            {showAddKid && (
                <AddKidDialog
                    open={showAddKid}
                    onOpenChange={setShowAddKid}
                    parentId={currentUser?.uid || ''}
                    onSuccess={() => {
                        setShowAddKid(false);
                        // Refresh children list
                        window.location.reload();
                    }}
                />
            )}
        </>
    );
};
